<template>
  <v-container fluid class="pa-0">
    <v-card class="mb-4 pa-4" outlined>
      <v-card-title class="pa-0 text-h6">Счета</v-card-title>
    </v-card>

    <v-card outlined>
      <v-card-text>
        <div v-if="pagination.totalPage > 1" class="d-flex justify-center my-4">
          <v-pagination
            v-model="pagination.current"
            :length="pagination.totalPage"
            :total-visible="7"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
          />
        </div>

        <v-data-table
          :headers="headers"
          :items="invoices"
          :loading="loading"
          loading-text="Загрузка…"
          class="elevation-0"
          dense
          hide-default-footer
          :items-per-page="pagination.perPage"
          :page="pagination.current"
          no-data-text="Счета не найдены"
        >
          <template #item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>
          <template #item.amount="{ item }">
            {{ formatAmount(item.amount) }}
          </template>
          <template #item.posted="{ item }">
            <v-chip v-if="item.is_draft" small color="grey" dark>
              Черновик
            </v-chip>
            <v-chip v-else small :color="item.posted ? 'success' : 'warning'" dark>
              {{ item.posted ? 'Проведён' : 'Не проведён' }}
            </v-chip>
          </template>
          <template #item.payment_status="{ item }">
            <v-chip small :color="paymentStatusColor(item.payment_status)" dark>
              {{ item.payment_status_label || 'Не оплачен' }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex justify-center" style="gap: 2px">
              <v-tooltip v-if="canEdit && item.id" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    x-small
                    color="info"
                    icon
                    v-on="on"
                    @click="onEdit(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Редактировать</span>
              </v-tooltip>
              <v-tooltip v-if="canDelete && item.id" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    x-small
                    color="error"
                    icon
                    v-on="on"
                    @click="onDelete(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Удалить</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>

        <div v-if="pagination.totalPage > 1" class="d-flex justify-center my-4">
          <v-pagination
            v-model="pagination.current"
            :length="pagination.totalPage"
            :total-visible="7"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
          />
        </div>
      </v-card-text>
    </v-card>

    <div v-if="canCreate" class="document-flow-invoices-fab">
      <v-tooltip left>
        <template #activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            color="success"
            dark
            fab
            v-on="on"
            @click="onCreate"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Создать счёт</span>
      </v-tooltip>
    </div>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title>Удалить счёт?</v-card-title>
        <v-card-text>
          <span v-if="invoiceToDelete && invoiceToDelete.is_draft">
            Черновик будет удалён в Statiz. В 1С он не отправлялся.
          </span>
          <span v-else>
            Счёт №{{ invoiceToDelete && invoiceToDelete.number }} будет удалён в Statiz и помечен на удаление в 1С.
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const PER_PAGE = 25

export default {
  name: 'DocumentFlowInvoicesList',

  data() {
    return {
      invoices: [],
      loading: false,
      pagination: {
        current: 1,
        totalPage: 0,
        perPage: PER_PAGE,
      },
      deleteDialog: false,
      invoiceToDelete: null,
      deleting: false,
      headers: [
        { text: '№ счета', value: 'number' },
        { text: 'Дата счета', value: 'date' },
        { text: 'Заказчик', value: 'project_name' },
        { text: 'Договор', value: 'contract_number' },
        { text: 'Приложение', value: 'appendix_number' },
        { text: 'Контрагент', value: 'counterparty_name' },
        { text: 'ИНН', value: 'counterparty_inn' },
        { text: 'КПП', value: 'counterparty_kpp' },
        { text: 'Сумма', value: 'amount', align: 'end' },
        { text: 'Статус', value: 'posted', align: 'center', sortable: false },
        { text: 'Статус оплаты', value: 'payment_status', align: 'center', sortable: false },
        { text: 'Действия', value: 'actions', align: 'center', sortable: false },
      ],
    }
  },

  computed: {
    ...mapGetters({
      hasPermission: 'user/hasPermission',
    }),
    canCreate() {
      return this.hasPermission('statiz.document-flow.invoices.create')
    },
    canEdit() {
      return this.hasPermission('statiz.document-flow.invoices.edit')
    },
    canDelete() {
      return this.hasPermission('statiz.document-flow.invoices.delete')
    },
  },

  mounted() {
    this.loadInvoices()
  },

  methods: {
    ...mapActions('document_flow/invoices', ['fetchInvoices', 'deleteInvoice']),

    paymentStatusColor(status) {
      if (status === 'paid') return 'success'
      if (status === 'partial') return 'warning'
      return 'grey'
    },

    async loadInvoices() {
      this.loading = true
      try {
        const response = await this.fetchInvoices()
        this.invoices = response.data || []
        this.pagination = {
          current: 1,
          totalPage: Math.ceil(this.invoices.length / PER_PAGE),
          perPage: PER_PAGE,
        }
      } catch (error) {
        this.invoices = []
        this.pagination.totalPage = 0
        this.$store.commit('shared/SET_ERROR', { message: 'Не удалось загрузить счета' }, { root: true })
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    onCreate() {
      this.$router.push({ name: 'document-flow.invoices.create' })
    },

    onEdit(item) {
      this.$router.push({
        name: 'document-flow.invoices.edit',
        params: { id: String(item.id) },
      })
    },

    onDelete(item) {
      this.invoiceToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.invoiceToDelete?.id) {
        return
      }

      this.deleting = true
      try {
        await this.deleteInvoice(this.invoiceToDelete.id)
        this.deleteDialog = false
        this.invoiceToDelete = null
        this.$store.commit('shared/SET_SUCCESS', { message: 'Счёт удалён' }, { root: true })
        await this.loadInvoices()
      } catch (error) {
        this.$store.commit('shared/SET_ERROR', { message: 'Не удалось удалить счёт' }, { root: true })
        console.error(error)
      } finally {
        this.deleting = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('ru-RU')
    },

    formatAmount(value) {
      const amount = Number(value || 0)
      return amount.toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style scoped>
.document-flow-invoices-fab {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 4;
}
</style>
